import React, { useCallback, useState } from "react";
import NormalInputField from "../../../../components/inputs/NormalInputField";
import { getToken, saveToken } from "../../../../utils/token";
import instance, { url } from "../../../../axios/instance";
import { useLoaderData, useNavigate } from "react-router-dom";
import NormalSelectInputField from "../../../../components/inputs/NormalSelectInputField";
import countries from "../../../../../countries.json";
import Spinner from "../../../../components/spinners/Spinner";
import { CropImage } from "../../../../components/modals/CropImage";
import { toast } from "react-toastify";
import { Edit, Edit2 } from "iconsax-react";
import StateLicenseInput from "../../../../components/inputs/StateLicenseInput";
import { convertLocaleStringToDateInputValue } from "../../../../utils/calculateAge";
import { CreditCard } from "lucide-react";
import CardInfoModal from "../../../../components/modals/CardModal";

export const settingsLoader = async ({ params }) => {
  const token = getToken();
  const res = await instance.get("/doctor", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data;
};

export const parentSettingsLoader = async ({ params }) => {
  const token = getToken();

  const res = await instance.get("/doctor", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const apiKeys = await instance.get("/doctor/api_keys/check", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return {
    doctor: res.data,
    apiKeys: apiKeys.data,
  };
};

export const SettingsPage = () => {
  const doctor = useLoaderData();
  const navigate = useNavigate();
  const [prefix, setPrefix] = useState(doctor.prefix || "");
  const [firstname, setFirstname] = useState(doctor.firstname || "");
  const [lastname, setLastname] = useState(doctor.lastname || "");
  const [gender, setGender] = useState(doctor.gender || "");
  const [dob, setDob] = useState(
    doctor.dob ? convertLocaleStringToDateInputValue(doctor.dob) : "",
  );
  const [degree, setDegree] = useState(doctor.degree || "");
  const [specialization, setSpecialization] = useState(
    doctor.specialization || "",
  );
  const [address1, setAddress1] = useState(doctor.address1 || "");
  const [address2, setAddress2] = useState(doctor.address2 || "");
  const [city, setCity] = useState(doctor.city || "");
  const [state, setState] = useState(doctor.state || "");
  const [country, setCountry] = useState(doctor.country || "");
  const [zipCode, setZipCode] = useState(doctor.zipcode || "");
  const [licenseNumber, setLicenseNumber] = useState(
    doctor.license_number || "",
  );
  const [deaNumber, setDeaNumber] = useState(doctor.dea_number || "");
  const [officePhone, setOfficePhone] = useState(doctor.work_phone || "");
  const [homePhone, setHomePhone] = useState(doctor.home_phone || "");
  const [email, setEmail] = useState(doctor.email || "");
  const [selectedStates, setSelectedStates] = useState(
    doctor.licensed_states ? JSON.parse(doctor.licensed_states) : [],
  );
  const [npiNumber, setNpiNumber] = useState(doctor.npi_number || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [openCardModal, setOpenCardModal] = useState(false);

  const getStates = (country) => {
    if (country) {
      const _country = countries.find(
        (_country) => _country.name.toLowerCase() === country.toLowerCase(),
      );
      return _country ? _country.states.map((state) => state.name) : [];
    }
    return [];
  };

  const btnDisabled =
    !firstname ||
    !lastname ||
    !prefix ||
    !gender ||
    !dob ||
    !npiNumber ||
    !degree ||
    !specialization ||
    !address1 ||
    !homePhone ||
    !officePhone ||
    !country ||
    !state ||
    !city ||
    !zipCode ||
    !licenseNumber ||
    !email ||
    selectedStates.length < 1;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setErrorMessage("");
    const token = getToken();
    setLoading(true);
    instance
      .put(
        "/doctor/edit",
        {
          email,
          firstname,
          lastname,
          gender,
          home_phone: homePhone,
          work_phone: officePhone,
          dob,
          address1,
          address2,
          npi_number: npiNumber,
          specialization,
          degree,
          prefix,
          dea_number: deaNumber,
          license_number: licenseNumber,
          zipcode: zipCode,
          licensed_states: JSON.stringify(selectedStates),
          city,
          state,
          country,
          password,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((response) => {
        setLoading(false);
        setSuccess(true);
        saveToken(response.data.ACCESS_TOKEN);
        window.location.reload();
      })
      .catch((error) => {
        console.log(error.response.data);
        setErrorMessage(error.response.data.message);
        setLoading(false);
      });
  };

  return (
    <>
      <div className={`max-h-[85vh] bg-white overflow-y-scroll`}>
        <div className="sticky left-0 right-0 top-0 z-10">
          <form onSubmit={handleFormSubmit}>
            <div className="px-7 py-10 grid grid-cols-1 gap-y-6">
              <div className="grid grid-cols-[250px_auto] gap-x-10">
                <UploadPicture
                  setError={setErrorMessage}
                  imgPath={doctor.profile_pic_path}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-6">
                  <NormalInputField
                    title="Prefix"
                    isRequired={true}
                    type="text"
                    onChange={(e) => setPrefix(e.target.value)}
                    value={prefix}
                  />
                  <NormalInputField
                    title="First name"
                    isRequired={true}
                    type="text"
                    onChange={(e) => setFirstname(e.target.value)}
                    value={firstname}
                  />
                  <NormalInputField
                    title="Last name"
                    isRequired={true}
                    type="text"
                    onChange={(e) => setLastname(e.target.value)}
                    value={lastname}
                  />
                  <NormalSelectInputField
                    title="Gender"
                    values={["Male", "Female"]}
                    isRequired={true}
                    onChange={(e) => setGender(e.target.value)}
                    value={gender}
                  />
                  <NormalInputField
                    title="D.O.B"
                    isRequired={true}
                    type="date"
                    onChange={(e) => setDob(e.target.value)}
                    value={dob}
                  />
                  <NormalInputField
                    title="Email"
                    isRequired={true}
                    type="email"
                    onChange={(e) => setEmail(e.target.value)}
                    value={email}
                  />
                  <NormalInputField
                    title="License number"
                    isRequired={true}
                    type="text"
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    value={licenseNumber}
                  />
                  <NormalInputField
                    title="DEA number"
                    isRequired={true}
                    type="text"
                    onChange={(e) => setDeaNumber(e.target.value)}
                    value={deaNumber}
                  />
                  <NormalInputField
                    title="npi number"
                    isRequired={true}
                    type="text"
                    onChange={(e) => setNpiNumber(e.target.value)}
                    value={npiNumber}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-6 items-baseline">
                <NormalInputField
                  title="Degree"
                  isRequired={true}
                  type="text"
                  onChange={(e) => setDegree(e.target.value)}
                  value={degree}
                />
                <NormalInputField
                  title="Specialization"
                  isRequired={true}
                  type="text"
                  onChange={(e) => setSpecialization(e.target.value)}
                  value={specialization}
                />
                <StateLicenseInput
                  selectedStates={selectedStates}
                  setSelectedStates={setSelectedStates}
                />
                <NormalInputField
                  title="Mobile Phone"
                  isRequired={true}
                  type="text"
                  onChange={(e) => setHomePhone(e.target.value)}
                  value={homePhone}
                />
                <NormalInputField
                  title="Office Phone"
                  isRequired={true}
                  type="text"
                  onChange={(e) => setOfficePhone(e.target.value)}
                  value={officePhone}
                />
                <NormalInputField
                  title="Password"
                  type="password"
                  onChange={(e) => setPassword(e.target.value)}
                  value={password}
                />
              </div>
              <div className="mt-8 grid grid-cols-1 gap-y-6">
                <div className="font-bold text-[16px]">Contact Details</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-6">
                  <NormalInputField
                    title="Address Line 1"
                    isRequired={true}
                    type="text"
                    onChange={(e) => setAddress1(e.target.value)}
                    value={address1}
                  />
                  <NormalInputField
                    title="Address Line 2"
                    isRequired={false}
                    type="text"
                    onChange={(e) => setAddress2(e.target.value)}
                    value={address2}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-6">
                  <NormalSelectInputField
                    title="Country"
                    isRequired={true}
                    values={countries.map((country) => country.name)}
                    onChange={(e) => setCountry(e.target.value)}
                    value={country}
                  />

                  <NormalSelectInputField
                    title="State"
                    isRequired={true}
                    values={getStates(country)}
                    onChange={(e) => setState(e.target.value)}
                    value={state}
                  />
                  <NormalInputField
                    title="City"
                    type="text"
                    isRequired={true}
                    onChange={(e) => setCity(e.target.value)}
                    value={city}
                  />
                  <NormalInputField
                    title="Zip Code"
                    isRequired={true}
                    type="text"
                    onChange={(e) => setZipCode(e.target.value)}
                    value={zipCode}
                  />
                </div>
              </div>

              {/* Card Details Section */}
              <div className="mt-8 pt-6 border-t">
                <div className="font-bold text-[16px] mb-3">
                  Personal Card Details
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="font-medium text-gray-900 text-sm flex items-center gap-2">
                      <CreditCard size={18} className="text-primary" />
                      <span>Payment & Billing Card</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Input or update your credit/debit card information used
                      for subscriptions and prescription order payments.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpenCardModal(true)}
                    className="cursor-pointer bg-primary text-white hover:opacity-90 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium text-[13px] shadow-sm transition-all whitespace-nowrap self-start sm:self-auto"
                  >
                    <CreditCard size={16} />
                    <span>Update Card Details</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-3 border-t">
              <div className="flex justify-between gap-2 items-center">
                <button
                  disabled={btnDisabled}
                  type="submit"
                  className={`cursor-pointer bg-primary text-white disabled:cursor-not-allowed disabled:opacity-50 min-w-[200px] flex justify-center items-center py-3 px-3 shadow-sm rounded-[4px] font-semibold text-[14px]`}
                >
                  {loading ? (
                    <span className="px-10 block">
                      <Spinner color="white" />
                    </span>
                  ) : (
                    "Save changes"
                  )}
                </button>
              </div>
              <div className="font-semibold text-[tomato] text-left text-[14px] mt-2">
                {errorMessage}
              </div>
              {success && (
                <div className="font-semibold text-[#3caf50] text-left text-[14px] mt-2">
                  Doctor information updated
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
      {openCardModal && (
        <CardInfoModal
          open={openCardModal}
          isOpen={openCardModal}
          onClose={() => setOpenCardModal(false)}
          address={address1}
          zipcode={zipCode}
          name={`${firstname} ${lastname}`.trim()}
          type={"doctor"}
        />
      )}
    </>
  );
};

export const UploadPicture = ({ setError, imgPath }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(
    imgPath ? `${url}${imgPath}` : null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle file input
  const handleFileInput = (e) => {
    console.log("opened");
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5000000) {
      setError("File size should be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
      setIsModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleImageSave = async (file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append("file", file);
    return await instance.put("/doctor/edit/picture", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });
  };

  return (
    <div className="relative flex flex-col items-center justify-center border-2 border-dashed border-primary rounded-full w-[250px] h-[250px] cursor-pointer hover:border-teal-800">
      <label className="cursor-pointer text-primary text-sm">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          className="hidden"
        />
        {!croppedImage ? (
          "Select a professional image"
        ) : (
          <>
            <Edit className="absolute z-10 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white" />
            <div className="bg-[rgba(0,0,0,.4)] rounded-full absolute inset-0"></div>
          </>
        )}
      </label>

      {croppedImage && (
        <img
          src={croppedImage}
          alt="Cropped Preview"
          className="rounded-full w-[250px] h-[250px] bg-cover bg-center object-cover"
        />
      )}

      {isModalOpen && (
        <CropImage
          setCroppedImage={setCroppedImage}
          imageSrc={imageSrc}
          setImageSrc={setImageSrc}
          setIsModalOpen={setIsModalOpen}
          handleFileSave={handleImageSave}
        />
      )}
    </div>
  );
};
